import React, { useEffect, useMemo } from "react";
import { MIXPANEL_EVENTS } from "../../config/mixPanelEvents";
import { userActivityLogger } from "../../utils/mixpanel";
import { AvatarCustom } from "..";
import { calculateDaysUntilExpiry, fromISOToFormatDate } from "../../utils";
import { Button, CircularProgress } from "@mui/material";
import { IScratchpayRenewalPending } from "../../services/get-alerts/get-alerts.interface";
import { useScratchPayRenewal } from "../../hooks/useScratchPayRenewal";

const scratchPayLogo = "/assets/icons/scratchpay.png";

interface IScratchPayRenewalPendingDashboardCardProps {
  renewalData: IScratchpayRenewalPending;
}

const getCardContent = (petName: string,
  planName: string,
  daysUntilExpiry: number,
  isExpired: boolean,
) => {
  if (isExpired) {
    return {
      title: (
        <>
          Renew <span className="capitalize">{petName}</span>&apos;s TotalCare Coverage
        </>
      ),
      description: (
        <>
          <span className="capitalize">{petName}</span>&apos;s current plan
            has expired. Complete your monthly {planName} enrollment now.
        </>
      ),
      buttonText: "Complete Purchase",
      showButton: true,
      dateLabel: "Coverage ended on",
    };
  }

  return {
    title: "Renewal Coming Up!",
    description: (
      <>
        <span className="capitalize">{petName}</span>&apos;s {planName} will
          be ready to activate in{" "}
        <span className="font-semibold text-light-black">
          {daysUntilExpiry} {daysUntilExpiry === 1 ? "day" : "days"}
        </span>
          . We&apos;ll remind you when it&apos;s time to complete your
          enrollment.
      </>
    ),
    buttonText: "",
    showButton: false,
    dateLabel: "Current plan ends on",
  };
};

const ScratchPayRenewalPendingDashboardCard = ({
  renewalData,
}: IScratchPayRenewalPendingDashboardCardProps) => {
  const { petId, petName, petImage, planName, endDate, planType, scratchpayPlanId, subscriptionId } = renewalData;

  const { handleApplyNow, preferenceLoading } = useScratchPayRenewal({
    petId,
    petName,
    planName,
    planType,
    subscriptionId,
    scratchpayPlanId,
  });

  const { daysUntilExpiry, isExpired } = useMemo(() => calculateDaysUntilExpiry(endDate), [endDate]);

  useEffect(() => {
    userActivityLogger(MIXPANEL_EVENTS.SCRATCHPAY_RENEWAL_PENDING_CARD_SHOWN, {
      pet_id: petId,
      pet_name: petName,
      plan_name: planName,
      current_plan_end_date: endDate,
      days_until_expiry: daysUntilExpiry,
      is_expired: isExpired,
    });
  }, [petId, petName, planName, endDate, daysUntilExpiry, isExpired]);


  const cardContent = getCardContent(petName, planName, daysUntilExpiry, isExpired);

  return (
    <div className="medium:py-6 medium:px-4 bg-violet-6 p-4 rounded-lg h-full">
      <div className="rounded-xl bg-white p-4 flex flex-col gap-3 relative h-full">
        <div className="flex flex-col gap-2">
          <div className="flex gap-4">
            <div className="shrink-0">
              <AvatarCustom
                width={80}
                height={80}
                image={petImage}
                name={petName}
                isWellnessMember={true}
                isBorder="white"
              />
            </div>
            <div className="flex flex-col justify-center self-center gap-1">
              <div className="!text-base font-semibold">{cardContent.title}</div>
              <div className="!text-xs font-normal grow">
                {cardContent.description}
              </div>
            </div>
          </div>

          {endDate && (
            <div className="text-sm font-semibold text-center leading-5">
              {cardContent.dateLabel} {fromISOToFormatDate(endDate)}
            </div>
          )}
        </div>

        <div className="flex items-center justify-center">
          {cardContent.showButton ? (
            <Button
              variant="contained"
              size="small"
              disabled={preferenceLoading}
              onClick={handleApplyNow}
              sx={{
                "width": "100%",
                "bgcolor": "info.light",
                "color": "black.main",
                "&:hover": {
                  "bgcolor": "gray.light",
                  "color": "black.main",
                },
              }}
            >
              {preferenceLoading ? <CircularProgress size={20} /> : cardContent.buttonText}
            </Button>
          ) : (
            <div className="flex items-center justify-center gap-1.5 text-xs text-gray-1">
              <span>powered by</span>
              <img
                src={scratchPayLogo}
                alt="ScratchPay"
                className="h-3.5"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScratchPayRenewalPendingDashboardCard;

